import { VerityAssessmentType, QuestionType } from "@verity/database/prisma";
import { ONE_MIN_MS, stripSeconds } from "../../../../helpers/time";
import i18n from "../../../../i18n";
import { AssessmentDuration } from "./AssessmentDetailsBlock";
import {
	AssessmentInfo,
	FITBBlankOptionInstructor,
	Lms,
	MatchingPairInstructor,
	MCOptionInstructor,
	Module,
	TFOptionInstructor,
	FileUploadOption,
	QuizQuestionInput,
} from "@verity/api-client/types";
import { ApiClientResponseError } from "@verity/api-client";
import { formatFileSize } from "../../../../helpers/file";
import apiClient from "../../../../helpers/api-client";
import { CoreFileItem } from "@verity/components/core";
import { Course } from "./hooks/useFetchVerityAssessmentInfo";
import { AddInstructionsReturn } from "./hooks/useAddInstructions";
import { AssessmentDetailsReturn } from "./hooks/useAssessmentDetails";
import { UseEditQuizStepReturn } from "./hooks/useEditQuizStep";
import { QuestionDeliveryReturn } from "./hooks/useQuestionDelivery";

export type QuizQuestionType = Extract<QuestionType, "MultipleChoice" | "TrueFalse" | "Matching" | "FillInTheBlank">;

export type QuizDraftQuestion = Omit<QuizQuestionInput, "questionType"> & {
	questionType: QuizQuestionType | null;
};

export const baseTransKey = "portal.components.verityAssessmentTSX";
export const UNLIMITED_ATTEMPTS = -1;
export const DEFAULT_NUM_ATTEMPTS = UNLIMITED_ATTEMPTS;
export const SHOW_ALL_QUESTIONS = -1;
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
export const MAX_FILE_UPLOAD = 1;
export const DEFAULT_ASSIGNMENT_MAX_SCORE = 100;

export type SaveErrorCode = "DB_FAIL" | "NO_COURSE" | "UPLOAD_SIZE" | "UPLOAD_FAIL";

export class AssessmentSaveError extends Error {
	constructor(
		public code: SaveErrorCode,
		message?: string,
	) {
		super(message);
		this.name = "AssessmentSaveError";
	}
}

// ── Question option types ────────────────────────────────────
export type MatchingDistractor = { response: string; weightage: 0 };

export function getDefaultQuestionPatch(type: QuizQuestionType): Partial<QuizDraftQuestion> {
	switch (type) {
		case "MultipleChoice":
			return {
				options: [
					{ text: i18n.t(`${baseTransKey}.questions.MultipleChoice.defaultOption_one`) as string, weightage: 1 },
					{ text: i18n.t(`${baseTransKey}.questions.MultipleChoice.defaultOption_two`) as string, weightage: 0 },
				] as MCOptionInstructor[],
			};
		case "TrueFalse":
			return {
				options: [
					{ text: "True", weightage: 1 },
					{ text: "False", weightage: 0 },
				] as TFOptionInstructor[],
			};
		case "FillInTheBlank":
			return {
				questionText: i18n.t(`${baseTransKey}.questions.editor.fitb.defaultQuestionText`, {
					blank: `<span class="question-blank" data-id="1"><span contenteditable="false">(${i18n.t(`${baseTransKey}.questions.editor.fitb.blankLabel`, { id: 1 })})</span></span>`,
				}) as string,
				options: [
					{
						blankId: 1,
						answers: [i18n.t(`${baseTransKey}.questions.editor.fitb.defaultAnswer`)] as string[],
						weightage: 1,
					},
				] as FITBBlankOptionInstructor[],
			};
		case "Matching":
			return {
				options: [
					{
						premise: i18n.t(`${baseTransKey}.questions.editor.matching.defaultPremise`, { index: 1 }) as string,
						response: i18n.t(`${baseTransKey}.questions.editor.matching.defaultResponse`, { index: 1 }) as string,
						weightage: 0.5,
					},
					{
						premise: i18n.t(`${baseTransKey}.questions.editor.matching.defaultPremise`, { index: 2 }) as string,
						response: i18n.t(`${baseTransKey}.questions.editor.matching.defaultResponse`, { index: 2 }) as string,
						weightage: 0.5,
					},
				] as MatchingPairInstructor[],
			};
	}
}

export type QuestionError =
	| { type: "emptyQuestionText"; questionIndex: number; description: string }
	| { type: "noOptions"; questionIndex: number; description: string }
	| { type: "emptyOptionText"; questionIndex: number; optionIndex: number; description: string }
	| { type: "noCorrectOption"; questionIndex: number; description: string }
	| { type: "emptyFitbBlanks"; questionIndex: number; description: string }
	| { type: "emptyFitbAnswer"; questionIndex: number; blankId: number; description: string }
	| { type: "emptyMatchingPremise"; questionIndex: number; optionIndex: number; description: string }
	| { type: "emptyMatchingResponse"; questionIndex: number; optionIndex: number; description: string };

export function getOptionLetter(index: number): string {
	return String.fromCharCode(65 + index);
}

export function validateQuestionsStep(questions: QuizDraftQuestion[]): QuestionError[] {
	const errors: QuestionError[] = [];

	questions.forEach((question, questionIndex) => {
		const { questionType, options, correctionPoints } = question;

		// 1. Question text is empty (strip HTML tags before checking)
		let sanitizedText = question.questionText;
		let previousSanitizedText: string;
		do {
			previousSanitizedText = sanitizedText;
			sanitizedText = sanitizedText.replace(/<[^>]*>/g, "");
		} while (sanitizedText !== previousSanitizedText);
		const strippedText = sanitizedText.trim();

		if (!strippedText) {
			errors.push({
				type: "emptyQuestionText",
				questionIndex,
				description: i18n.t(`${baseTransKey}.questions.editor.error.emptyQuestionText`, {
					questionNo: questionIndex + 1,
				}) as string,
			});
		}

		if (questionType === "MultipleChoice") {
			const mcOptions = (options as MCOptionInstructor[] | undefined) ?? [];
			// 2. No options at all
			if (mcOptions.length === 0) {
				errors.push({
					type: "noOptions",
					questionIndex,
					description: i18n.t(`${baseTransKey}.questions.editor.error.noOptions`, { questionNo: questionIndex + 1 }),
				});
			}
			// 3. Any option has empty text — report which option index
			mcOptions.forEach((o, optionIndex) => {
				if (!o.text.trim()) {
					errors.push({
						type: "emptyOptionText",
						questionIndex,
						optionIndex,
						description: i18n.t(`${baseTransKey}.questions.editor.error.emptyOptionText`, {
							questionNo: questionIndex + 1,
							optionNo: optionIndex + 1,
						}) as string,
					});
				}
			});
			// 4. Graded but no correct option selected
			if (!!correctionPoints && !mcOptions.some((o) => o.weightage > 0)) {
				errors.push({
					type: "noCorrectOption",
					questionIndex,
					description: i18n.t(`${baseTransKey}.questions.editor.error.noCorrectOption`, {
						questionNo: questionIndex + 1,
					}) as string,
				});
			}
		} else if (questionType === "TrueFalse") {
			const tfOptions = (options as TFOptionInstructor[] | undefined) ?? [];
			// 5. Graded but neither True nor False marked correct
			if (!!correctionPoints && !tfOptions.some((o) => o.weightage > 0))
				errors.push({
					type: "noCorrectOption",
					questionIndex,
					description: i18n.t(`${baseTransKey}.questions.editor.error.noCorrectOption`, {
						questionNo: questionIndex + 1,
					}) as string,
				});
		} else if (questionType === "FillInTheBlank") {
			const fitbBlanks = (options as FITBBlankOptionInstructor[] | undefined) ?? [];
			// 6. No blanks inserted in question text
			if (fitbBlanks.length === 0) {
				errors.push({
					type: "emptyFitbBlanks",
					questionIndex,
					description: i18n.t(`${baseTransKey}.questions.editor.error.emptyFitbBlanks`, {
						questionNo: questionIndex + 1,
					}) as string,
				});
			}
			// 7. Graded but at least one blank has no non-empty accepted answers — report which blankId
			if (correctionPoints) {
				fitbBlanks.forEach((b) => {
					if (b.answers.length === 0 || b.answers.every((a) => !a.trim())) {
						errors.push({
							type: "emptyFitbAnswer",
							questionIndex,
							blankId: b.blankId,
							description: i18n.t(`${baseTransKey}.questions.editor.error.emptyFitbAnswer`, {
								questionNo: questionIndex + 1,
								blankId: b.blankId,
							}) as string,
						});
					}
				});
			}
		} else if (questionType === "Matching") {
			const matchingOptions = (options as Array<MatchingPairInstructor | MatchingDistractor> | undefined) ?? [];
			// 8. Any pair has an empty premise or response — report which optionIndex and which field
			matchingOptions.forEach((opt, optionIndex) => {
				if (!("premise" in opt)) {
					const distractor = opt;
					if (!distractor.response.trim()) {
						errors.push({
							type: "emptyMatchingResponse",
							questionIndex,
							optionIndex,
							description: i18n.t(`${baseTransKey}.questions.editor.error.emptyMatchingResponse`, {
								questionNo: questionIndex + 1,
								responseNo: optionIndex + 1,
							}) as string,
						});
					}
					return;
				} else {
					const pair = opt;
					if (!pair.premise.trim()) {
						errors.push({
							type: "emptyMatchingPremise",
							questionIndex,
							optionIndex,
							description: i18n.t(`${baseTransKey}.questions.editor.error.emptyMatchingPremise`, {
								questionNo: questionIndex + 1,
								premiseNo: optionIndex + 1,
							}) as string,
						});
					}
					if (!pair.response.trim()) {
						errors.push({
							type: "emptyMatchingResponse",
							questionIndex,
							optionIndex,
							description: i18n.t(`${baseTransKey}.questions.editor.error.emptyMatchingResponse`, {
								questionNo: questionIndex + 1,
								responseNo: optionIndex + 1,
							}) as string,
						});
					}
				}
			});
		}
	});

	return errors;
}

export function isAssessmentTitleValid(title?: string) {
	const trimmedTitle = title?.trim() ?? "";
	return trimmedTitle.length > 0 && trimmedTitle.length <= 128; // D2L has a 128 character limit for quiz titles
}

export function getCourse(courses: Course[], courseId: number | undefined): Course | undefined {
	return courses.find((course) => course.id === courseId);
}

export function isSelectedCourseValid(selectedCourseId: number | undefined, courses: Course[]) {
	return selectedCourseId !== undefined && courses.some((course) => course.id === selectedCourseId);
}

export function isSelectedModuleValid(selectedModuleId: string | undefined, modules: Module[]) {
	return selectedModuleId !== undefined && modules.some((module) => module.id === selectedModuleId);
}

export function isAssignmentPointsValid(points?: number | string): boolean {
	if (points === undefined) return true;
	if (typeof points === "string" && points.trim() === "") {
		return false;
	}
	const numericPoints = typeof points === "string" ? Number(points) : points;
	if (isNaN(numericPoints)) return false;
	return numericPoints >= 0 && numericPoints <= 1000;
}

export function getAssignmentPointsErrorMessage(points?: number | string): string | undefined {
	if (isAssignmentPointsValid(points)) {
		return undefined;
	}
	return i18n.t(`${baseTransKey}.assessmentDetails.courseGrade.assignmentMaxScoreError`) as string;
}

export function isDurationUnlimited(duration: AssessmentDuration | number | null) {
	if (typeof duration === "number") {
		return duration === UNLIMITED_ATTEMPTS;
	}
	return (
		duration === null ||
		(duration.hour === String(UNLIMITED_ATTEMPTS) && duration.minute === String(UNLIMITED_ATTEMPTS))
	);
}

export function isDurationNotSet(duration: AssessmentDuration | number | null): boolean {
	if (typeof duration === "number") {
		return duration === 0;
	}
	if (duration === null) return false;
	return duration.hour === "0" && duration.minute === "0";
}

export function convertNumberToDuration(duration: number | null): AssessmentDuration | null {
	if (duration === null) return null;
	const hour = Math.floor(duration / 60);
	const minute = duration % 60;

	return { hour: String(hour), minute: String(minute) };
}

export function convertDurationToNumber(duration: AssessmentDuration | null): number {
	if (duration === null) return 0;
	return Number(duration.hour) * 60 + Number(duration.minute);
}

export function isNumAttemptsSet(numAttempts: number | null): boolean {
	if (numAttempts === null) return false;
	return numAttempts !== 1;
}

const ASSESSMENT_DURATION_ERRORS = [
	"invalidHour",
	"invalidMinute",
	"invalidHourInput",
	"invalidMinuteInput",
	"noDuration",
] as const;
type AssessmentDurationError = (typeof ASSESSMENT_DURATION_ERRORS)[number];

export function validateAssessmentDuration(
	duration: AssessmentDuration | number | null,
): AssessmentDurationError[] | undefined {
	const cleanedDuration = typeof duration === "number" ? convertNumberToDuration(duration) : duration;
	if (cleanedDuration === null) return undefined;

	const errors: AssessmentDurationError[] = [];
	const { hour, minute } = cleanedDuration;
	const isHourValid = Number.isInteger(Number(hour));
	const isMinuteValid = Number.isInteger(Number(minute));

	// Validate Hour
	if (!isHourValid || hour.trim() === "") {
		errors.push("invalidHourInput");
	}
	if (isHourValid && Number(hour) < 0) {
		errors.push("invalidHour");
	}

	if (!isMinuteValid || minute.trim() === "") {
		errors.push("invalidMinuteInput");
	}

	// Validate Minute
	if (isMinuteValid && (Number(minute) < 0 || Number(minute) > 59)) {
		errors.push("invalidMinute");
	}
	if (isDurationNotSet(cleanedDuration)) errors.push("noDuration");
	return errors.length > 0 ? errors : undefined;
}

export function getAssessmentDurationErrorMessage(error?: AssessmentDurationError): string | undefined {
	switch (error) {
		case "invalidHour":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.invalidHour`) as string;
		case "invalidMinute":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.invalidMinute`) as string;
		case "noDuration":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.noDuration`) as string;
		case "invalidHourInput":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.invalidHourInput`) as string;
		case "invalidMinuteInput":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.invalidMinuteInput`) as string;
		default:
			return undefined;
	}
}

export function isHourError(error?: AssessmentDurationError): boolean {
	return error === "invalidHour" || error === "invalidHourInput";
}

export function isMinuteError(error?: AssessmentDurationError): boolean {
	return error === "invalidMinute" || error === "invalidMinuteInput" || error === "noDuration";
}

const DUE_DATE_ERRORS = ["noStartDate", "noEndDate", "endBeforeStart", "endInPast"] as const;
type DueDateError = (typeof DUE_DATE_ERRORS)[number];

export function validateDueDate(startDate: Date | null, endDate: Date | null): DueDateError[] | undefined {
	const errors: DueDateError[] = [];
	if (!startDate) errors.push("noStartDate");
	if (!endDate) errors.push("noEndDate");
	if (startDate && endDate && endDate <= startDate) errors.push("endBeforeStart");
	if (endDate && endDate < new Date()) errors.push("endInPast");
	return errors.length > 0 ? errors : undefined;
}

export function getDueDateErrorMessage(error?: DueDateError): string | undefined {
	switch (error) {
		case "noStartDate":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.noStartDate`) as string;
		case "noEndDate":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.noEndDate`) as string;
		case "endBeforeStart":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.endBeforeStart`) as string;
		case "endInPast":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.endInPast`) as string;
		default:
			return undefined;
	}
}

export function isEndDateError(error?: DueDateError): boolean {
	return error === "noEndDate" || error === "endBeforeStart" || error === "endInPast";
}

export function isStartDateError(error?: DueDateError): boolean {
	return error === "noStartDate";
}

export function getInitialDueDates(): { startDate: Date; endDate: Date } {
	const now = new Date();

	// 10 minutes from current time
	const startDate = stripSeconds(new Date(now.getTime() + 15 * ONE_MIN_MS));

	// 1 week from start date at 11:59 PM
	const endDate = new Date(startDate.getTime());
	endDate.setDate(endDate.getDate() + 7);

	// 3. Snap End Date to 11:59 PM
	endDate.setHours(23, 59, 0, 0);

	return { startDate, endDate };
}

const LATE_SUBMISSION_ERRORS = ["noLateSubmissionDate", "lateSubmissionBeforeEnd", "lateSubmissionInPast"] as const;
type LateSubmissionError = (typeof LATE_SUBMISSION_ERRORS)[number];

export function validateLateSubmissionDate(
	endDate: Date | null,
	lateSubmissionDate: Date | null,
): LateSubmissionError[] | undefined {
	const errors: LateSubmissionError[] = [];
	if (!lateSubmissionDate) {
		errors.push("noLateSubmissionDate");
	} else {
		const now = new Date();
		if (endDate && lateSubmissionDate <= endDate) errors.push("lateSubmissionBeforeEnd");
		if (lateSubmissionDate < now) errors.push("lateSubmissionInPast");
	}
	return errors.length > 0 ? errors : undefined;
}

export function getLateSubmissionErrorMessage(error?: LateSubmissionError): string | undefined {
	switch (error) {
		case "noLateSubmissionDate":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.noLateSubmissionDate`) as string;
		case "lateSubmissionBeforeEnd":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.lateSubmissionBeforeEnd`) as string;
		case "lateSubmissionInPast":
			return i18n.t(`${baseTransKey}.assessmentDetails.timing.error.lateSubmissionInPast`) as string;
		default:
			return undefined;
	}
}

export function getInitialLateSubmissionDate(endDate: Date): Date {
	// Default late submission date is 3 days after end date at 11:59 PM
	const lateSubmissionDate = new Date(endDate.getTime());
	lateSubmissionDate.setDate(lateSubmissionDate.getDate() + 3);
	lateSubmissionDate.setHours(23, 59, 0, 0);
	return lateSubmissionDate;
}

type AssessmentDetailsError =
	| "invalidTitle"
	| "invalidCourse"
	| "invalidModule"
	| "invalidPoints"
	| DueDateError
	| LateSubmissionError
	| AssessmentDurationError;

export function validateAssessmentDetailsStep(
	details: AssessmentDetailsReturn,
	courses: Course[],
	modules: Module[],
	lms: Lms | undefined,
): AssessmentDetailsError[] {
	const errors: AssessmentDetailsError[] = [];

	if (!isAssessmentTitleValid(details.generalInfo.assessmentTitle)) errors.push("invalidTitle");
	if (!isSelectedCourseValid(details.course.selectedCourseId, courses)) errors.push("invalidCourse");
	if (usesModules(lms) && !isSelectedModuleValid(details.course.selectedModuleId, modules))
		errors.push("invalidModule");
	if (!isAssignmentPointsValid(details.course.assignmentMaxScoreInput)) errors.push("invalidPoints");

	const dueDateErrors = validateDueDate(details.timing.startDate, details.timing.endDate);
	if (dueDateErrors) errors.push(...dueDateErrors);

	const lateSubmissionErrors = details.timing.allowLateSubmissions
		? validateLateSubmissionDate(details.timing.endDate, details.timing.lateSubmissionDate)
		: undefined;
	if (lateSubmissionErrors) errors.push(...lateSubmissionErrors);

	const assessmentDurationErrors =
		details.timing.assessmentDuration !== null
			? validateAssessmentDuration({
					hour: details.timing.durationHourInput,
					minute: details.timing.durationMinInput,
				})
			: undefined;
	if (assessmentDurationErrors) errors.push(...assessmentDurationErrors);

	return errors;
}

export function isFileTooLarge(file: CoreFileItem): boolean {
	return file.size > MAX_FILE_SIZE_BYTES;
}

type AddInstructionsError = "fileTooLarge" | "tooManyFiles";

export function validateAddInstructionStep(files: AddInstructionsReturn["uploadedFiles"]): AddInstructionsError[] {
	const errors: AddInstructionsError[] = [];
	if (files.length > MAX_FILE_UPLOAD) {
		errors.push("tooManyFiles");
	}
	files.forEach((file) => {
		if (isFileTooLarge(file)) {
			errors.push("fileTooLarge");
			return;
		}
	});

	return errors;
}

export function getAddInstructionErrorMessage(error?: AddInstructionsError): string | undefined {
	switch (error) {
		case "fileTooLarge":
			return i18n.t(`${baseTransKey}.addInstructions.fileTooLarge`, {
				size: formatFileSize(MAX_FILE_SIZE_BYTES),
			}) as string;
		case "tooManyFiles":
			return i18n.t(`${baseTransKey}.addInstructions.tooManyFiles`) as string;
		default:
			return undefined;
	}
}

export function getFileTooBigErrorMessage(file?: CoreFileItem): string | undefined {
	if (file && !isFileTooLarge(file)) {
		return undefined;
	}

	return i18n.t(`${baseTransKey}.addInstructions.fileTooLarge`, {
		size: formatFileSize(MAX_FILE_SIZE_BYTES),
	}) as string;
}

export function getFileDiff(initialFiles: CoreFileItem[], currentFiles: CoreFileItem[]) {
	const addedFiles = currentFiles.filter((file) => !isFileInitialFiles(file, initialFiles));
	const removedFiles = initialFiles.filter(
		(initialFile) => !currentFiles.some((file) => isFileInitialFiles(file, [initialFile])),
	);

	return { addedFiles, removedFiles };
}

function isFileInitialFiles(file: CoreFileItem, initialFiles: CoreFileItem[]): boolean {
	return initialFiles.some((initialFile) => initialFile.name === file.name && initialFile.size === file.size);
}

export function getModule(modules: Module[], moduleId: string | undefined): Module | undefined {
	return modules.find((module) => module.id === moduleId);
}

export function usesModules(lms?: Lms): boolean {
	return lms === "desire2learn" || lms === "moodle";
}

export function getInitialCompletedSteps(isEditing: boolean): number[] {
	return isEditing ? [0, 1, 2, 3] : [];
}

export function getInitialTitle(now?: Date): string {
	const currTime = now ?? new Date();

	const datePart = new Intl.DateTimeFormat(i18n.language, {
		year: "numeric",
		month: "long",
		day: "numeric",
	}).format(currTime);

	const timePart = new Intl.DateTimeFormat(i18n.language, {
		hour: "numeric",
		minute: "numeric",
		hour12: true,
	}).format(currTime);

	const defaultTitle = i18n.t(`${baseTransKey}.assessmentDetails.generalInfo.defaultTitle`, {
		date: datePart,
		time: timePart,
	}) as string;

	return defaultTitle;
}

export function mapQuizQuestionToAssignment(question?: QuizQuestionInput | QuizQuestionInput[]) {
	if (!question) {
		return undefined;
	}

	const firstQuestion = Array.isArray(question) ? question[0] : question;

	if (firstQuestion.questionType !== "FileUpload") {
		return undefined;
	}

	const fileOptions = firstQuestion.options as FileUploadOption | undefined;

	const allowedFileTypes =
		fileOptions?.allowedFileTypes.map((fileType) => ({
			value: fileType,
			label: fileType,
		})) ?? [];

	return {
		instructions: firstQuestion.questionText,
		allowAllFiles: allowedFileTypes.length === 0,
		allowedFileTypes,
		assignmentMaxScore: firstQuestion.correctionPoints,
	};
}

/**Cleans Verity assessment options before saving to db*/
function prepareVerityAssessmentOption(
	type: VerityAssessmentType,
	details: AssessmentDetailsReturn,
	questionDelivery: QuestionDeliveryReturn,
	publish: boolean,
): SaveOptionsType["verityAssessmentOptions"] {
	const cleanedDetails = { ...details };
	const isAssignment = type === "assignment";

	// Set lateSubmissionDate to null if allowLateSubmissions is not enabled
	if (!details.timing.allowLateSubmissions && cleanedDetails.timing.lateSubmissionDate) {
		cleanedDetails.timing.lateSubmissionDate = null;
	}

	// If assessment duration is not set (ex: 0), set to null for db.
	if (details.timing.assessmentDuration !== null && isDurationNotSet(details.timing.assessmentDuration)) {
		cleanedDetails.timing.assessmentDuration = null;
	}

	// If allowMultipleAttempts is false but numberOfAttempts is set to a value other than default, reset to default for db
	if (!details.submission.allowMultipleAttempts && isNumAttemptsSet(details.submission.numberOfAttempts)) {
		cleanedDetails.submission.numberOfAttempts = DEFAULT_NUM_ATTEMPTS;
	}

	const verityAssessmentOptions: SaveOptionsType["verityAssessmentOptions"] = {
		allowLateSubmission: cleanedDetails.timing.allowLateSubmissions,
		lateSubmissionDate: cleanedDetails.timing.lateSubmissionDate?.toISOString() ?? null,
		duration: cleanedDetails.timing.assessmentDuration,
		description: cleanedDetails.generalInfo.assessmentDescription,
		endTime: cleanedDetails.timing.endDate.toISOString(),
		startTime: cleanedDetails.timing.startDate.toISOString(),
		numAttempts: cleanedDetails.submission.numberOfAttempts,
		showScoreOnSubmit: isAssignment ? null : cleanedDetails.submission.showScoreOnSubmission,
		type: type,
		questionsPerPage: isAssignment ? null : questionDelivery.questionsPerPage,
		shuffleQuestions: isAssignment ? null : questionDelivery.shuffleQuestions,
		syncGradebook: cleanedDetails.course.isSynced,
		moduleId: cleanedDetails.course.selectedModuleId,
		isPublished: publish,
		scoringStrategy: cleanedDetails.submission.scoringStrategy,
	};
	return verityAssessmentOptions;
}

function prepareQuizQuestions(
	type: VerityAssessmentType,
	assessmentDetails: AssessmentDetailsReturn,
	addInstructions: AddInstructionsReturn,
	quizQuestions?: QuizDraftQuestion[],
) {
	let targetQuizQuestions: QuizQuestionInput[] | undefined;

	if (type === "assignment") {
		let allowedFileTypes: string[];

		if (assessmentDetails.submission.allowAllFiles) {
			// If all file types are allowed, set allowedFileTypes to empty array for db
			allowedFileTypes = [];
		} else {
			// Remove leading . if present
			allowedFileTypes = assessmentDetails.submission.allowedFileTypes.map((fileType) => {
				return fileType.value.trim().toLowerCase().replace(/^\./, "");
			});
		}

		targetQuizQuestions = [
			{
				questionType: "FileUpload",
				questionText: addInstructions.instructions,
				questionOrder: 1,
				correctionPoints: assessmentDetails.course.assignmentMaxScore ?? DEFAULT_ASSIGNMENT_MAX_SCORE,
				options: {
					allowedFileTypes,
				} as FileUploadOption,
			},
		];
	} else {
		targetQuizQuestions = quizQuestions?.filter(
			(q): q is QuizDraftQuestion & { questionType: QuestionType } => q.questionType !== null,
		);
	}

	return targetQuizQuestions;
}

type SaveOptionsType = Parameters<typeof apiClient.proctoringConfiguration.save>[0];

type SaveVerityAssessmentProps = {
	type: VerityAssessmentType;
	assessmentDetails: AssessmentDetailsReturn;
	addInstructions: AddInstructionsReturn;
	questionDelivery: QuestionDeliveryReturn;
	editQuizData: UseEditQuizStepReturn["editQuizData"];
	quizInfo: AssessmentInfo;
	quizQuestions?: QuizDraftQuestion[];
	publish: boolean;
};

export async function saveVerityAssessment({
	type,
	assessmentDetails,
	addInstructions,
	questionDelivery,
	editQuizData,
	quizInfo,
	quizQuestions,
	publish,
}: SaveVerityAssessmentProps): Promise<{
	verityQuizId: number;
	lmsQuizId: string;
}> {
	const verityAssessmentOptions = prepareVerityAssessmentOption(type, assessmentDetails, questionDelivery, publish);

	const mappedQuizQuestions = prepareQuizQuestions(type, assessmentDetails, addInstructions, quizQuestions);

	const options: SaveOptionsType = {
		assessmentInfo: quizInfo,
		profileId: editQuizData?.activeProfile?.id,
		proctoringAssessmentConfiguration: {
			...editQuizData?.proctoringAssessmentConfiguration,
			enabled: true,
		},
		proctoringOption: editQuizData?.proctoringOption,
		verityAssessmentOptions,
		quizQuestions: mappedQuizQuestions,
	};

	const response = await apiClient.proctoringConfiguration.save(options);
	const lmsQuizId = response.assessmentConfiguration?.lmsQuiz?.quizId;
	const verityQuizId = response.verityAssessmentInfo?.quizId;

	if (!lmsQuizId || !verityQuizId) {
		throw new Error("Failed to save assessment configuration: Missing quiz IDs in response");
	}

	return { verityQuizId, lmsQuizId };
}

export async function uploadAssessmentResource(file: File, quizId: number): Promise<void> {
	try {
		const response = await apiClient.proctoringConfiguration.getAssessmentResourceUploadUrl({
			quizId,
			fileName: file.name,
			mimeType: file.type,
			fileSize: file.size,
		});

		const url = response.uploadUrl;

		const uploadResponse = await fetch(url, {
			method: "PUT",
			headers: {
				"Content-Type": file.type,
			},
			body: file,
		});
		if (!uploadResponse.ok) {
			throw new AssessmentSaveError(
				"UPLOAD_FAIL",
				`Failed to upload assessment resource: ${uploadResponse.statusText}`,
			);
		}
	} catch (e) {
		if (e instanceof AssessmentSaveError) throw e;
		if (e instanceof ApiClientResponseError) {
			if (e.status === 413) throw new AssessmentSaveError("UPLOAD_SIZE");
		}
		throw new AssessmentSaveError("UPLOAD_FAIL");
	}
}

export async function deleteAssessmentResource(quizId: number, fileName: string): Promise<void> {
	try {
		await apiClient.proctoringConfiguration.deleteAssessmentResource({ quizId, fileName });
	} catch (e) {
		throw new AssessmentSaveError("UPLOAD_FAIL");
	}
}
